import { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eraser, Check, RotateCcw, Pen, Image } from 'lucide-react';

/**
 * Composant de signature canvas
 * Permet de dessiner une signature ou reutiliser une signature enregistree
 */
const SignatureCanvas = ({ 
  onSave, 
  onCancel, 
  width = 400, 
  height = 200,
  strokeColor = '#0F172A',
  strokeWidth = 2,
  backgroundColor = '#FFFFFF',
  loading = false,
  buttonText = 'Valider la signature',
  existingSignature = null
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [context, setContext] = useState(null);
  const [useExisting, setUseExisting] = useState(!!existingSignature);

  useEffect(() => {
    if (useExisting) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setContext(ctx);
    }
  }, [width, height, strokeColor, strokeWidth, backgroundColor, useExisting]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    context.beginPath();
    context.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    context.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, width, height);
    context.strokeStyle = strokeColor;
    setHasDrawn(false);
  };

  const saveSignature = () => {
    if (useExisting && existingSignature) {
      onSave(existingSignature);
    } else {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSave(dataUrl);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
        <span className="ml-3 text-slate-600">Signature en cours...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Choix : signature existante ou nouvelle */}
      {existingSignature && (
        <div className="w-full flex gap-2 p-1 bg-slate-100 rounded-lg" data-testid="signature-mode-selector">
          <button
            type="button"
            onClick={() => setUseExisting(true)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              useExisting ? 'bg-white shadow text-[#0F172A]' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="use-existing-signature-btn"
          >
            <Image className="h-4 w-4" />
            Ma signature enregistrée
          </button>
          <button
            type="button"
            onClick={() => { setUseExisting(false); setHasDrawn(false); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              !useExisting ? 'bg-white shadow text-[#0F172A]' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="draw-new-signature-btn"
          >
            <Pen className="h-4 w-4" />
            Dessiner une nouvelle
          </button>
        </div>
      )}

      {useExisting && existingSignature ? (
        <>
          <div className="border-2 border-green-300 rounded-lg overflow-hidden bg-white p-2" data-testid="existing-signature-preview">
            <img 
              src={existingSignature} 
              alt="Ma signature" 
              className="max-w-full h-auto" 
              style={{ maxHeight: `${height}px` }}
            />
          </div>
          <p className="text-xs text-green-600 font-medium">Signature enregistrée lors de votre inscription</p>
        </>
      ) : (
        <>
          {!existingSignature && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Pen className="h-4 w-4" />
              <span>Dessinez votre signature ci-dessous</span>
            </div>
          )}
          <div 
            className="border-2 border-slate-300 rounded-lg overflow-hidden shadow-inner"
            style={{ touchAction: 'none' }}
          >
            <canvas
              ref={canvasRef}
              width={width}
              height={height}
              className="cursor-crosshair"
              style={{ 
                width: '100%', 
                maxWidth: `${width}px`,
                height: 'auto',
                aspectRatio: `${width}/${height}`
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="signature-canvas"
            />
          </div>
          <p className="text-xs text-slate-400">
            {hasDrawn ? 'Signature tracée' : 'Aucune signature'}
          </p>
        </>
      )}

      {/* Boutons d'action */}
      <div className="flex items-center gap-3">
        {!useExisting && (
          <Button
            type="button"
            variant="outline"
            onClick={clearCanvas}
            className="flex items-center gap-2"
            data-testid="clear-signature-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Effacer
          </Button>
        )}
        
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            data-testid="cancel-signature-btn"
          >
            Annuler
          </Button>
        )}
        
        <Button
          type="button"
          onClick={saveSignature}
          disabled={!useExisting && !hasDrawn}
          className="bg-[#8DC63F] hover:bg-[#7db336] flex items-center gap-2"
          data-testid="save-signature-btn"
        >
          <Check className="h-4 w-4" />
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default SignatureCanvas;
